/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button, Spacer, Table, Text } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { ManageRoles } from "~/components/Role/ManageRoles";
import { UnassignRole } from "~/components/Role/UnassignRole";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import { EmployeeRole } from "~/models";
import { generateActionSuccessText } from "~/tools";
import { useAxios } from "~/tools/hooks/useAxios";
import { Role } from "./types";

type Props = {
  customerId: string;
  employeeId: string;
  employeeRoles: EmployeeRole[];
  header: string;
  refetch: () => void;
};

function EditAssignMemberSidebar({
  customerId,
  employeeId,
  employeeRoles,
  header,
  refetch,
}: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const methods = useForm();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const [roles, setRoles] = React.useState([]);
  const fetchRoles = React.useCallback(async () => {
    try {
      const { data } = await Axios().get("/roles?limit=100");
      setRoles(data?.roles);
    } catch (e) {
      showError(e.response);
    }
  }, [Axios, showError]);

  React.useEffect(() => {
    isSidebarOpen && fetchRoles();
  }, [fetchRoles, isSidebarOpen]);

  const onSubmit = async ({ role }: { role: string }) => {
    const organizeValues = { employeeId, roleIds: [role] };
    try {
      await Axios().post(`/customers/${customerId}/employees`, organizeValues);
      setIsSidebarOpen(false);
      refetch();
      showSuccess(generateActionSuccessText(`Employee`)()(`added`)());
    } catch (e) {
      showError(e.response);
    } finally {
      clean();
    }
  };
  const clean = () => {
    refetch();
    setIsSidebarOpen(false);
  };

  const findRoles = employeeRoles.map((r: EmployeeRole) => r.id);
  const filterRoles =
    roles && roles.filter(({ id }) => !findRoles.includes(id));

  const roleOpts =
    filterRoles?.map(({ name, id }: Role) => ({
      label: name,
      value: id,
    })) || [];

  return (
    <>
      <Button
        icon="settings"
        size="sm"
        onClick={() => setIsSidebarOpen(true)}
        severity="low"
      >
        Role
      </Button>

      <SidebarForm
        title="Edit Assignment"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <Text tag="div">{header}</Text>

        <Table small hoverline>
          <Table.thead>
            <Table.tr>
              <Table.th css={{ width: "100%" }}>Role Name</Table.th>
              <Table.th>unassign</Table.th>
            </Table.tr>
          </Table.thead>
          <Table.tbody>
            {employeeRoles.map((r: EmployeeRole) => (
              <Table.tr key={`${r.id}${r.name}`}>
                <Table.td css={{ width: "100%" }}>{r.name}</Table.td>
                <Table.td>
                  <UnassignRole
                    id={customerId}
                    role={r}
                    employeesId={employeeId}
                    deleteCallback={clean}
                  />
                </Table.td>
              </Table.tr>
            ))}
          </Table.tbody>
        </Table>
        <Spacer height="2rem" />

        <SidebarDropdown name="role" label="Role" options={roleOpts} required />

        <FlexContainer>
          <Button
            disabled={!methods.watch("role")}
            css={{ marginRight: "auto" }}
          >
            Assign
          </Button>
          <ManageRoles fetchTeam={refetch} refetchRoles={fetchRoles} />
        </FlexContainer>
      </SidebarForm>
    </>
  );
}

export { EditAssignMemberSidebar };
